/**
 * AI Metrics Panel
 * Display learning metrics and fine-tuning statistics
 */

import React, { useState, useEffect } from 'react'
import { Brain, BarChart3, TrendingUp, Trash2, AlertCircle } from 'lucide-react'
import { aiContextManager } from '@/services/aiContextManager'
import aiService from '@/services/aiService'

export default function AIMetricsPanel({ systemStatus }) {
  const [metrics, setMetrics] = useState(null)
  const [userPattern, setUserPattern] = useState(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [clearing, setClearing] = useState(false)

  useEffect(() => {
    loadMetrics()
    const interval = setInterval(loadMetrics, 5000)
    return () => clearInterval(interval)
  }, [])

  const loadMetrics = () => {
    try {
      const m = aiContextManager.getLearningMetrics()
      setMetrics(m)
      const pattern = aiContextManager.getUserPattern('default')
      setUserPattern(pattern)
    } catch (error) {
      console.error('Failed to load metrics:', error)
    }
  }

  const handleClearData = async () => {
    setClearing(true)
    try {
      await aiService.clearLearningData()
      loadMetrics()
      setShowClearConfirm(false)
    } catch (error) {
      console.error('Failed to clear data:', error)
    } finally {
      setClearing(false)
    }
  }

  if (!metrics || !userPattern) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-400">Loading metrics...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Learning Statistics */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-400" />
          Learning Metrics
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Interactions */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Total Interactions</span>
              <TrendingUp className="w-4 h-4 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-blue-400">{metrics.totalInteractions}</p>
            <p className="text-xs text-slate-500 mt-1">tracked and analyzed</p>
          </div>

          {/* Average Rating */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Average Rating</span>
              <BarChart3 className="w-4 h-4 text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-yellow-400">{metrics.averageRating}/5.0</p>
            <p className="text-xs text-slate-500 mt-1">user satisfaction</p>
          </div>

          {/* User Profiles */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Learned Patterns</span>
              <Brain className="w-4 h-4 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-purple-400">{metrics.userProfiles}</p>
            <p className="text-xs text-slate-500 mt-1">unique user profiles</p>
          </div>
        </div>

        {/* Top Tags */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Top Feedback Tags</h3>
          <div className="space-y-2">
            {metrics.topTags && metrics.topTags.length > 0 ? (
              metrics.topTags.map((tag, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-slate-300">{tag.tag}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: `${Math.min((tag.count / Math.max(...metrics.topTags.map(t => t.count), 1)) * 100, 100)}%`,
                        }}
                      />
                    </div>
                    <span className="text-slate-400 text-sm w-12 text-right">{tag.count}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-sm">No feedback tags yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Learned User Pattern */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          Learned User Pattern (Default User)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              label: 'Preferred Tone',
              value: userPattern.preferredTone,
              options: ['professional', 'casual', 'friendly', 'formal'],
            },
            {
              label: 'Detail Level',
              value: userPattern.detailLevel,
              options: ['brief', 'moderate', 'detailed'],
            },
            {
              label: 'Communication Style',
              value: userPattern.communicationStyle,
              options: ['direct', 'conversational', 'technical'],
            },
            {
              label: 'Response Length',
              value: userPattern.responseLength,
              options: ['short', 'moderate', 'long'],
            },
            {
              label: 'Technical Level',
              value: userPattern.technicalLevel,
              options: ['basic', 'intermediate', 'advanced'],
            },
          ].map((item, idx) => (
            <div key={idx} className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
              <p className="text-slate-400 text-sm mb-2">{item.label}</p>
              <p className="text-white font-medium">{item.value}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.options.map(opt => (
                  <span
                    key={opt}
                    className={`text-xs px-2 py-1 rounded ${
                      opt === item.value
                        ? 'bg-blue-500/30 text-blue-300 border border-blue-400'
                        : 'bg-slate-600 text-slate-400 border border-slate-500'
                    }`}
                  >
                    {opt}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
          <p className="text-sm text-cyan-300">
            💡 These patterns are learned from your interactions and feedback. The AI adapts its responses based on these preferences over time.
          </p>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          Learning Data Management
        </h2>

        <div className="space-y-4">
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <h3 className="text-white font-medium mb-2">Privacy Controls</h3>
            <p className="text-slate-300 text-sm mb-4">
              Your learning data is stored locally in your browser. You can clear it anytime to reset the AI's memory and learned patterns.
            </p>

            {!showClearConfirm ? (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Clear All Learning Data
              </button>
            ) : (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 space-y-3">
                <p className="text-red-300 font-medium">⚠ Are you sure?</p>
                <p className="text-red-200 text-sm">
                  This will permanently delete all feedback, user patterns, and learning data. This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleClearData}
                    disabled={clearing}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
                  >
                    {clearing ? 'Clearing...' : 'Yes, Clear All Data'}
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    disabled={clearing}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fine-Tuning Status */}
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-purple-300 mb-3">
          Fine-Tuning Your Local Model
        </h3>
        <div className="text-sm text-purple-100 space-y-2">
          <p>✓ Your interactions and feedback are being collected for model improvement</p>
          <p>✓ {Math.min(systemStatus.feedbackCount || 0, 100)} / 100 training examples accumulated</p>
          <p>✓ Once you have 100+ examples, export and fine-tune with: <code className="bg-slate-800 px-2 py-1 rounded text-xs">ollama create custom-model --from neural-chat</code></p>
          <p>✓ See AI_SETUP_GUIDE.md for complete fine-tuning workflow</p>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-xs text-slate-500 text-center">
        Last updated: {new Date(metrics.lastUpdated).toLocaleString()}
      </div>
    </div>
  )
}
